import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BridgeListResult } from 'app/providers/bridge-api.service';
import { StorageAPIService } from 'app/providers/storage-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { urlFor } from '../storage-api/utils';
import { Widget, WidgetGroup } from './dashboard.service';

export interface SavedViewBackendModel {
  id: string;
  project_id: string;
  account_id: string;
  user_id: string;
  dashboard_id: string;
  dashboard_revision: number;
  creator: string;
  viewName: string;
  created_at_date: string;
  modified_at_date: string;
  widgets: Widget[];
  widget_groups: { [key: string]: WidgetGroup };
}

export type SavedViewSearchResult = BridgeListResult<SavedViewBackendModel>;

export interface SavedViewCreateOrUpdatePayload {
  dashboardRevision?: number;
  viewName?: string;
  widgets?: Widget[];
  widgetGroups?: { [key: string]: WidgetGroup };
}

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class SavedViewService extends StorageAPIService {
  constructor(
    public http: HttpClient,
    public systemService: SystemService
  ) {
    super(http, systemService);
  }

  public getSavedViews(projectId: string, dashboardId: string): Promise<SavedViewSearchResult> {
    const endpoint: string = urlFor(`/dashboards/${dashboardId}/saved-views`, projectId);
    return this.get<SavedViewSearchResult>(endpoint);
  }

  public getSavedViewById(
    projectId: string,
    dashboardId: string,
    savedViewId: string
  ): Promise<SavedViewBackendModel> {
    const endpoint: string = urlFor(
      `/dashboards/${dashboardId}/saved-views/${savedViewId}`,
      projectId
    );
    return this.get<SavedViewBackendModel>(endpoint);
  }

  public createSavedView(
    projectId: string,
    dashboardId: string,
    payload: SavedViewCreateOrUpdatePayload
  ): Promise<SavedViewBackendModel> {
    const endpoint: string = urlFor(`/dashboards/${dashboardId}/saved-views`, projectId);
    return this.post<SavedViewBackendModel>(endpoint, {
      dashboardRevision: payload.dashboardRevision,
      viewName: payload.viewName,
      widgets: payload.widgets ?? [],
      widgetGroups: payload.widgetGroups ?? {},
    });
  }

  public updateSavedView(
    projectId: string,
    dashboardId: string,
    savedViewId: string,
    payload: SavedViewCreateOrUpdatePayload
  ): Promise<SavedViewBackendModel> {
    const endpoint: string = urlFor(
      `/dashboards/${dashboardId}/saved-views/${savedViewId}`,
      projectId
    );
    return this.put<SavedViewBackendModel>(endpoint, {
      dashboardRevision: payload.dashboardRevision,
      viewName: payload.viewName,
      widgets: payload.widgets,
      widgetGroups: payload.widgetGroups,
    });
  }

  public deleteSavedView(
    projectId: string,
    dashboardId: string,
    savedViewId: string
  ): Promise<void> {
    const endpoint: string = urlFor(
      `/dashboards/${dashboardId}/saved-views/${savedViewId}`,
      projectId
    );
    return this.delete<void>(endpoint);
  }
}
